import { AfterViewInit, Directive, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { CompiereDataGridRequestJSON } from '@compiere-ws/models/compiere-data-json';
import { ActiveTabManagerService } from '@iupics-manager/managers/active-tab-manager/active-tab-manager.service';
import { UICreatorService } from '@iupics-manager/managers/ui-creator/ui-creator.service';
import { IupicsWidget } from '@iupics-manager/models/iupics-widget';
import { SubscriptionsUtils } from '@iupics-util/tools/subscriptions.util';
import { ColumnState } from '@iupics/apiz-grid';
import { WorkspaceService } from '@web-desktop/components/workspace/components/workspace-ui/workspace.service';
import { UrlParamsService } from '@web-desktop/controllers/url-params.service';
import { IupicsMenuType } from '@web-desktop/models/menu-item-ui';
import { interval, Subscription } from 'rxjs';

@Directive()
export abstract class AbstractWidgetComponent implements AfterViewInit, OnDestroy {
  @Input() isPreview = false;
  @Input() resizeWidgetEmitter = new EventEmitter<any>();
  protected _widget: IupicsWidget;

  @Input()
  set widget(value) {
    // l'utilisation du setter permet de réagir à l'update du widget.
    this._widget = value;
    this.updateWidget();
  }

  get widget() {
    return this._widget;
  }

  subscriptions: Subscription[] = [];
  tableName: string;
  @Output() openTabEmitter = new EventEmitter<any>();
  @Output() widgetChanged = new EventEmitter<any>();

  readonly #workspaceService = inject(WorkspaceService);
  readonly #urlParamsService = inject(UrlParamsService);
  readonly #activeTabManager = inject(ActiveTabManagerService);

  constructor(protected uiCreator: UICreatorService) {}

  ngAfterViewInit(): void {
    if (!this.isPreview && this.widget.refreshRate > 0) {
      this.subscriptions.push(
        interval(this.widget.refreshRate * 1000).subscribe(() => {
          if (this.#activeTabManager.isDashboardDisplayed()) {
            this.updateWidget();
          }
        })
      );
    }
  }

  ngOnDestroy(): void {
    SubscriptionsUtils.unsubscribe(...this.subscriptions);
  }

  getDataGridRequest(): CompiereDataGridRequestJSON {
    if (this.widget.gridRequest) {
      return JSON.parse(this.widget.gridRequest);
    } else {
      return null;
    }
  }

  getDataGridState(): ColumnState[] {
    if (this.widget.gridState) {
      return JSON.parse(this.widget.gridState);
    } else {
      return null;
    }
  }

  openTab(event: MouseEvent, item?: { id: string; displayedName: string }): void {
    if (event?.stopPropagation) {
      event.stopPropagation();
    }

    if (!this.isPreview) {
      const id = item?.id;
      let recordId = null;

      if (id) {
        recordId = id.split(',')[1];
        this.zoomAcross(recordId, this.tableName, this.widget.windowId);
      } else if (this.widget.windowId) {
        this.#urlParamsService.updateUrlParams({
          initGridParams: {
            request: this.getDataGridRequest(),
            state: this.getDataGridState(),
          },
        });
        this.#workspaceService.openTargetSearchEmt.emit({
          zoomInfo: null,
          cat: { id: this.widget.windowId },
          source: {
            id: null,
          },
        });
      }
    }
  }

  /* Zoom Across */
  zoomAcross(record_id: any, tableName: string, windowId: number, openNewRecord = false) {
    this.subscriptions.push(
      this.uiCreator.zoomAcross(tableName, tableName + '_ID', record_id ?? null, true, windowId).subscribe({
        next: (dataWs) => {
          if (dataWs && dataWs.length > 0) {
            const zoomInfo = {
              windowId: dataWs[0].Window_ID,
              dataUUID: dataWs[dataWs.length - 1]['Record_ID'],
              record_id: record_id ?? null,
              children: dataWs.length > 1 ? dataWs.splice(0, dataWs.length - 1) : null,
              isInCurrentWindow: true,
            };

            const source = {
              id: null,
              menuType: IupicsMenuType.WINDOW,
            };

            if (record_id) {
              source.id = dataWs[0]?.Record_ID ?? null;
            }

            if (openNewRecord) {
              source.id = 'newRecord';
            }
            this.#urlParamsService.updateUrlParams({ initGridParams: { request: this.getDataGridRequest() ?? null } });
            this.#workspaceService.openTargetSearchEmt.emit({
              zoomInfo: zoomInfo,
              cat: { id: parseInt(dataWs[0].Window_ID, 10) },
              source,
              ctx: null,
            });
          }
        },
      })
    );
  }

  abstract updateWidget(): void;
}
